import fs from 'fs-extra'
import { IAppConfig, IBaseOptions, ResponseFormat } from '../../interfaces/index.js'

/** The default base URL for the Congress API. */
export const DEFAULT_BASE_URL = 'https://api.congress.gov/v3'

/** The default format for the data returned by the API. */
export const DEFAULT_RESPONSE_FORMAT = ResponseFormat.JSON

/** The default directory name to use for storing files. */
export const DEFAULT_DATA_DIR = 'data'

/**
 * If the request has more than one page of results, this is the default number
 * of results to return per page.
 */
export const DEFAULT_REQUEST_LIMIT = 20

/**
 * If the request has more than one page of results, this is the default number
 * of items to offset the results by.
 */
export const DEFAULT_REQUEST_OFFSET = 0

/** A default value to use for the `api_key` query param. */
export const DEFAULT_API_KEY = 'NoKeyProvided'

/** The default value for the `trimmedResponses` parameter. */
export const DEFAULT_TRIMMED_RESPONSES = true

/** The default value for the `debug` parameter. */
export const DEFAULT_DEBUG = false

/**
 * The default options for all requests. These options can be overridden by the
 * user with any values provided to the request methods individually.
 */
export const DEFAULT_REQUEST_OPTIONS: IBaseOptions = {
  pathParams: [],

  queryParams: {
    api_key: DEFAULT_API_KEY,
    format: DEFAULT_RESPONSE_FORMAT,
    limit: DEFAULT_REQUEST_LIMIT,
    offset: DEFAULT_REQUEST_OFFSET
  }
}

/**
 * The default values to use for the library config options. The values are as
 * follows:
 *
 * - responseFormat → Value of `DEFAULT_RESPONSE_FORMAT` constant (`json`).
 * - baseUrl → Value of `DEFAULT_BASE_URL` constant (`https://api.congress.gov/v3`).
 * - dataDir → Value of `DEFAULT_DATA_DIR` constant (`data`).
 * - apiKey → Value of `DEFAULT_API_KEY` constant (`NoKeyProvided`).
 * - trimmedResponses → Value of `DEFAULT_TRIMMED_RESPONSES` constant (`true`).
 * - debug → Value of `DEFAULT_DEBUG` constant (`false`).
 */
export const DEFAULT_APP_CONFIG: Required<IAppConfig> = {
  responseFormat: DEFAULT_RESPONSE_FORMAT,
  baseUrl: DEFAULT_BASE_URL,
  dataDir: DEFAULT_DATA_DIR,
  apiKey: DEFAULT_API_KEY,
  trimmedResponses: DEFAULT_TRIMMED_RESPONSES,
  debug: DEFAULT_DEBUG
}

/**
 * The default format to use for the date string in a filename. The value is
 * `YYYY.MM.DD-HH.mm.ss`, which is passed to the [`Dayjs`][0] library for formatting
 * purposes.
 *
 * [0]: https://day.js.org
 */
export const DEFAULT_FILENAME_DATE_FORMAT = 'YYYY.MM.DD-HH.mm.ss'

/**
 * The default options to use when writing to the filesystem. The following
 * values are passed to the `writeJSON` method of the [`fs-extra`][0] library.
 *
 * - `encoding` → `utf8`
 * - `spaces` → `2`
 *
 * [0]: https://github.com/jprichardson/node-fs-extra
 */
export const DEFAULT_FS_WRITE_OPTIONS: fs.WriteOptions = {
  spaces: 2,
  encoding: 'utf8'
}
